<template>
  <section>
    <el-collapse v-model="isOpen">
      <el-collapse-item
        :title="$t('common.search')"
        name="bill-of-lading-search-section"
        id="bill-of-lading-search-section"
      >
        <el-form
          id="bill-of-lading-search-section-form"
          class="search-section-form"
          :model="searchForm"
          ref="searchForm"
          label-position="right"
          size="mini"
          inline
        >
          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.dateFrom')">
                <el-date-picker
                  class="el-dropdown-new drop-sp date-dob"
                  @change="handleSearch"
                  v-model="searchForm.dateFrom"
                  format="MM/dd/yyyy"
                  type="date"
                  id="searchForm-dateFrom"
                  ref="filter-dateFrom"
                  :clearable="false"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.dateTo')">
                <el-date-picker
                  class="el-dropdown-new drop-sp date-dob"
                  @change="handleSearch"
                  v-model="searchForm.dateTo"
                  format="MM/dd/yyyy"
                  clearable
                  type="date"
                  id="searchForm-dateTo"
                  ref="filter-dateTo"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.bolNumber')">
                <el-input
                  v-model="searchForm.bolNumber"
                  v-alphanumeric-validation
                  maxlength="20"
                  clearable
                  @clear="handleSearch"
                  v-on:keyup.enter.native="handleSearch"
                  class="inline-input"
                  ref="filter-bolNumber"
                  id="searchForm-bolNumber"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.deliverTo')">
                <autocomplete
                  :model.sync="searchForm.deliverToAccountId"
                  :shipperAccountId="null"
                  labelFieldLastWithDash="number"
                  popperAppendToBody
                  :url="urlConsignee"
                  :selectAction="handleSearch"
                  :enterKeyAction="handleSearch"
                  :clearAction="handleSearch"
                  labelField="name"
                  valueField="id"
                  ref="filter-deliverToAccountId"
                  id="searchForm-deliverToAccountId"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.consignee')">
                <autocomplete
                  :model.sync="searchForm.consigneeAccountId"
                  :shipperAccountId="null"
                  labelFieldLastWithDash="number"
                  popperAppendToBody
                  :url="urlConsignee"
                  :selectAction="handleSearch"
                  :enterKeyAction="handleSearch"
                  :clearAction="handleSearch"
                  labelField="name"
                  valueField="id"
                  ref="filter-consigneeAccountId"
                  id="searchForm-consigneeAccountId"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.billTo')">
                <autocomplete
                  :model.sync="searchForm.billToAccountId"
                  :shipperAccountId="null"
                  labelFieldLastWithDash="number"
                  popperAppendToBody
                  :url="urlBillTo"
                  :selectAction="handleSearch"
                  :enterKeyAction="handleSearch"
                  :clearAction="handleSearch"
                  labelField="name"
                  valueField="id"
                  ref="filter-billToAccountId"
                  id="searchForm-billToAccountId"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.loadNumber')">
                <el-input
                  v-model="searchForm.loadNumber"
                  v-alphanumeric-validation
                  maxlength="20"
                  clearable
                  @clear="handleSearch"
                  v-on:keyup.enter.native="handleSearch"
                  class="inline-input"
                  ref="filter-loadNumber"
                  id="searchForm-loadNumber"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.state')">
                <state-select v-model="searchForm.stateId" @change="handleSearch" :includeOptionAll="true" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.city')">
                <el-input
                  v-model="searchForm.city"
                  v-alphanumeric-validation
                  maxlength="20"
                  clearable
                  @clear="handleSearch"
                  v-on:keyup.enter.native="handleSearch"
                  class="inline-input"
                  ref="filter-city"
                  id="searchForm-city"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('common.features')">
                <el-select
                  v-model="featuresSelected"
                  multiple
                  collapse-tags
                  @change="handleSearch"
                >
                  <el-option
                    v-for="item in features"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.billOfLading.deleted')">
                <el-select v-model="searchForm.active" @change="handleSearch">
                  <el-option
                    v-for="item in deletedOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="24" class="text-align-end">
              <el-tooltip
                :content="$t('common.search')"
                class="item"
                effect="dark"
                placement="top-start"
              >
                <el-button
                  id="bill-of-lading-search-section-form-button"
                  class="button-action-default"
                  @click="handleSearch"
                  icon="el-icon-search"
                  size="mini"
                ></el-button>
              </el-tooltip>
            </el-col>
          </el-row>
        </el-form>
      </el-collapse-item>
    </el-collapse>
  </section>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import Autocomplete from '@/components/Autocomplete';
import StateSelect from '@/components/StateSelect';
import constants from '@/utils/constants';
import apiConstants from '@/utils/apiConstants';

export default {
  name: 'BillOfLadingSearchSection',
  components: { Autocomplete, StateSelect },
  computed: {
    ...mapGetters(['billOfLading']),
    deletedOptions() {
      /* Inverted value intended
       * Deleted: true -> Active: false
       * Deleted: false -> Active: true
      */
      return [
        {
          value: constants.STATUS_BOOLEAN.yes.value,
          label: constants.STATUS_BOOLEAN.no.label
        },
        {
          value: constants.STATUS_BOOLEAN.no.value,
          label: constants.STATUS_BOOLEAN.yes.label
        }
      ];
    },
    features() {
      return constants.FILTER_FEATURES;
    }
  },
  data() {
    return {
      activeCollapsibleName: ['bill-of-lading-search-section'],
      isOpen: ['bill-of-lading-search-section'],
      searchForm: {
        deliverToAccountId: null,
        consigneeToAccountId: null,
        billToAccountId: null,
        dateFrom: new Date(),
        dateTo: null,
        bolNumber: null,
        loadNumber: null,
        stateId: null,
        city: null,
        active: true
      },
      urlConsignee: apiConstants.END_POINTS.ACCOUNTS.CONSIGNEE,
      urlBillTo: apiConstants.END_POINTS.ACCOUNTS.BILL_TO,
      featuresSelected: [],
    };
  },
  watch: {
    'searchForm.dateFrom': function updateTo(newVal) {
      // to work with changes in someOtherProp
      const newDateFrom = moment(newVal);
      if (newDateFrom.isAfter(moment(this.searchForm.dateTo))) {
        this.searchForm.dateTo = newVal;
      }
    },
    'searchForm.dateTo': function updateFrom(newVal) {
      const newDateTo = moment(newVal);
      if (newDateTo.isBefore(moment(this.searchForm.dateFrom))) {
        this.searchForm.dateFrom = newVal;
      }
    }
  },
  methods: {
    handleSearch() {
      const featureFields = {};
      for (const feature of this.features) {
        featureFields[feature.value] = this.featuresSelected.some(featureSelected => feature.value === featureSelected);
      }

      this.$store.dispatch('billOfLading/search', { ...this.searchForm, ...featureFields });
    }
  },
  mounted() {
    this.handleSearch();
  }
};
</script>
